import React, { memo, useEffect, useMemo, useState } from 'react';
import { NestableNavigationContent, Section } from '@atlaskit/side-navigation';
import { CONNECT_ROUTES_SET } from '@atlassian/jira-common-constants/src/spa-routes-sets.tsx';
import { useCurrentRoute } from '@atlassian/jira-platform-router-utils/src/index.tsx';
import type { JiraSpaRoute } from '@atlassian/react-resource-router';
import { SECTION_ID, FORGE_ROUTES_SET } from '../../../../../common/constants/index.tsx';
import type { Project } from '../../../../../common/types/index.tsx';
import useAutoExpandSidebar from '../../../../../controllers/auto-expand-sidebar/index.tsx';
import {
	useProjectSettingsConnectItems,
	useProjectSettingsForgeItems,
} from '../../../../../controllers/index.tsx';
import { SettingsMenu as SettingsMenuNextGen } from './settings-nextgen/index.tsx';
import { Settings } from './settings/main.tsx';

const EMPTY_STACK: string[] = [];

const adjustAppStack = (appStack: Array<string>) => {
	const [first, ...other] = appStack;

	// omit the settings nesting level as we render the settings menu
	// as a top level menu in business projects
	if (SECTION_ID.SETTINGS === first) {
		return other;
	}

	return appStack;
};

const calculateStack = (
	route: JiraSpaRoute,
	connectStack: null | Array<string>,
	forgeStack: null | Array<string>,
) => {
	// should be handled above generic PROJECT_SETTINGS_CORE logic
	if (FORGE_ROUTES_SET.has(route.name) && forgeStack) {
		return adjustAppStack(forgeStack);
	}

	if (route.name.includes('project-settings-core')) {
		// this is the same logic as the one in src/packages/navigation-apps/sidebar/next-gen/src/ui/menu/main.js
		const [view] = route.name.replace(`${'project-settings-core'}-`, '').split('-');
		return view === 'issuetypes' ? [view] : EMPTY_STACK;
	}

	if (CONNECT_ROUTES_SET.has(route.name) && connectStack) {
		return adjustAppStack(connectStack);
	}

	return EMPTY_STACK;
};

type Props = {
	project: Project;
};

const SettingsMenu = ({ project }: Props) => {
	useAutoExpandSidebar();
	const route = useCurrentRoute();
	const { data: connectItems, stack: connectStack } = useProjectSettingsConnectItems();
	const { data: forgeItems, stack: forgeStack } = useProjectSettingsForgeItems(
		'project-settings-core-forge-apps',
		EMPTY_STACK,
	);
	const ecosystemItems = useMemo(
		() => ({
			forge: forgeItems,
			connect: connectItems,
		}),
		[forgeItems, connectItems],
	);

	const [stack, setStack] = useState<string[]>(() =>
		calculateStack(route, connectStack, forgeStack),
	);

	useEffect(() => {
		setStack(calculateStack(route, connectStack, forgeStack));
	}, [route, connectStack, forgeStack]);

	return (
		<NestableNavigationContent stack={stack} onChange={setStack}>
			<Section>
				{project.isSimplified ? (
					<SettingsMenuNextGen stack={stack} />
				) : (
					<>
						<Settings ecosystemItems={ecosystemItems} />
					</>
				)}
			</Section>
		</NestableNavigationContent>
	);
};

export default memo<Props>(SettingsMenu);
